// app/src/Shell.jsx — app shell + navigation for Kanjo Crew.
//
// CrewNav context carries the current route + navigate(). KShell renders the
// phone-framed surface (root `.crew` element — the splash watches for it) with a
// header and a bottom tab bar (Board / Chat / Schedule / Pay / You). Mirrors the
// Kanjo PWA shell pattern; theming comes entirely from styles/tokens.css.

const CrewNav = React.createContext({ current: '/board', navigate: () => {} });

function useNav() { return React.useContext(CrewNav); }

const TABS = [
  { key: 'board', route: '/board', label: () => t('nav.board', 'Board') },
  { key: 'chat', route: '/chat', label: () => t('nav.chat', 'Chat') },
  { key: 'schedule', route: '/schedule', label: () => t('nav.schedule', 'Schedule') },
  { key: 'pay', route: '/pay', label: () => t('nav.pay', 'Pay') },
  { key: 'you', route: '/you', label: () => t('nav.you', 'You') },
];

// Line icons for the bottom nav (inherit color via currentColor).
const TAB_ICONS = {
  board: <React.Fragment><rect x="3" y="4" width="18" height="16" rx="2.5" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="7" y1="13.5" x2="14" y2="13.5" /><line x1="7" y1="16.5" x2="17" y2="16.5" /></React.Fragment>,
  chat: <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.6-.8L3 21l1.9-5.4A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />,
  schedule: <React.Fragment><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="8" y1="2.5" x2="8" y2="6" /><line x1="16" y1="2.5" x2="16" y2="6" /></React.Fragment>,
  pay: <React.Fragment><rect x="3" y="6" width="18" height="13" rx="2.5" /><path d="M3 9.5h18" /><circle cx="16.5" cy="13.8" r="1.3" fill="currentColor" stroke="none" /></React.Fragment>,
  you: <React.Fragment><circle cx="12" cy="8.5" r="3.5" /><path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" /></React.Fragment>,
};

function KHeader({ title, back, right }) {
  const nav = useNav();
  return (
    <header style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '14px 16px', borderBottom: '1px solid var(--line-on)', flex: 'none',
    }}>
      {back && (
        <button onClick={() => nav.back ? nav.back() : nav.navigate('/board')}
          style={{ border: 'none', background: 'none', color: 'var(--on)', fontSize: 20, cursor: 'pointer' }}>‹</button>
      )}
      <div style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--on)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      {right}
    </header>
  );
}

// Bell icon for the header with an unread badge. `count` drives both the in-app
// badge and the OS app-icon badge (PWA Badging API, where supported). Pass an
// onClick to make it actionable (e.g. jump to the Tasks filter).
function NotifBell({ count = 0, onClick }) {
  React.useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    try { count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge(); } catch { /* badging unsupported */ }
  }, [count]);
  const has = count > 0;
  return (
    <button onClick={onClick} aria-label={t('notif.bell', 'Notifications')} title={t('notif.bell', 'Notifications')}
      style={{ position: 'relative', border: 'none', background: 'none', color: 'var(--on)', cursor: onClick ? 'pointer' : 'default', padding: 2, lineHeight: 0, flex: 'none' }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.5 21a1.8 1.8 0 0 1-3 0" />
      </svg>
      {has && (
        <span style={{
          position: 'absolute', top: -3, right: -3, minWidth: 15, height: 15, padding: '0 4px',
          borderRadius: 999, background: 'var(--primary)', color: 'var(--primary-ink)',
          fontSize: 9, fontWeight: 800, lineHeight: '15px', textAlign: 'center',
          border: '1.5px solid var(--bg)', boxSizing: 'border-box',
        }}>{count > 9 ? '9+' : count}</span>
      )}
    </button>
  );
}

function BottomNav({ tab }) {
  const nav = useNav();
  return (
    <nav style={{
      flex: 'none', display: 'flex', padding: '8px 4px 14px',
      background: 'var(--surface)', borderTop: '1px solid var(--line)',
    }}>
      {TABS.map((tdef) => {
        const active = tdef.key === tab;
        return (
          <button key={tdef.key} onClick={() => nav.navigate(tdef.route)}
            style={{
              flex: 1, border: 'none', background: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              color: active ? 'var(--primary)' : 'var(--sub)',
              fontWeight: 700, fontSize: 9.5, letterSpacing: '.2px',
            }}>
            <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {TAB_ICONS[tdef.key]}
            </svg>
            {tdef.label()}
          </button>
        );
      })}
    </nav>
  );
}

/** The framed app surface. `tab` highlights the active bottom-nav item. */
function KShell({ tab = 'board', children }) {
  return (
    <div className="crew" style={{
      height: '100%', display: 'flex', justifyContent: 'center',
    }}>
      <div style={{
        width: '100%', maxWidth: 430, height: '100%',
        display: 'flex', flexDirection: 'column', background: 'transparent',
      }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
        <BottomNav tab={tab} />
      </div>
    </div>
  );
}

Object.assign(window, { CrewNav, useNav, KShell, KHeader, BottomNav, NotifBell });
